import { useContext, useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth0 } from "@auth0/auth0-react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { isIOS, isAndroid, isWebNotCapacitor } from "src/utils/platformUtils";
import SecondGetStartedPage from "./SecondGetStartedPage";
import { HelpMenuMain } from "./HelpMenuMain";
import { WidgetContext } from "src/contexts/WidgetContext";
import ContactUs from "./ContactUs";
import Faq from "./Faq";
import Glossary from "./Glossary";
import WhatsNew from "./WhatsNew";
import ReadTermsAndConditions from "./ReadTermsAndConditions";
import ReportBug from "./ReportBug";
import SuggestFeature from "./SuggestFeature";
// Typeform Feedback popup (kept for rollback)
// import { openRotoGPTFeedbackPopup } from "./FeedbackApp";
import FeedbackForm from "./FeedbackForm";
import { promptNativeAppReview } from "src/services/appReview";

interface HelpMenuProps {
    isOpen?: boolean;
    onClose?: () => void;
    showButton?: boolean;
}

/** Desktop SEO/GEO: update the address bar without React Router navigation (keeps HomePage mounted). */
function pushDesktopHelpUrl(path: string) {
    if (typeof window === "undefined") return;
    if (`${window.location.pathname}${window.location.search}` === path) return;
    window.history.pushState({ helpDesktopSeo: true }, "", path);
}

const HelpMenu = ({ isOpen: externalIsOpen, onClose: externalOnClose, showButton = true }: HelpMenuProps = {}) => {
    const { isLoggedInAuth0ExperienceGlobal, aiSettings, NON_LOGGED_IN_CONFIG } = useContext(WidgetContext);
    const { user, getAccessTokenSilently, isAuthenticated: auth0IsAuthenticated } = useAuth0();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [activeView, setActiveView] = useState<string>("menu");
    const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
    const [accessAuthToken, setAccessAuthToken] = useState<string | null>(null);
    const desktopReturnPathRef = useRef<string | null>(null);

    // Same authId as ContactUs: for RotoGPT feedback popup hidden field user_id
    const authId = user?.sub || NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;

    // Use external state if provided, otherwise use internal state
    const isGetStartedDrawerOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const handleClose = () => {
        setActiveView("menu"); // Reset to menu when closing
        if (isWebNotCapacitor() && desktopReturnPathRef.current) {
            const returnPath = desktopReturnPathRef.current;
            desktopReturnPathRef.current = null;
            window.history.pushState(null, "", returnPath);
        }
        if (externalOnClose) {
            externalOnClose();
        } else {
            setInternalIsOpen(false);
        }
    };
    const handleOpen = () => {
        if (externalIsOpen === undefined) {
            setInternalIsOpen(true);
        }
    };

    const handleBack = () => {
        if ((activeView === "faq" || activeView === "glossary") && isWebNotCapacitor() && desktopReturnPathRef.current) {
            window.history.pushState(null, "", desktopReturnPathRef.current);
        }
        setActiveView("menu");
    };

    const handleChangeContent = (content: string) => {
        setActiveView(content);
        if (isWebNotCapacitor() && isGetStartedDrawerOpen) {
            if (content === "faq") pushDesktopHelpUrl("/faq");
            else if (content === "glossary") pushDesktopHelpUrl("/glossary");
        }
    };

    // Desktop web only: remember URL before drawer opens (for restore on close/back).
    useEffect(() => {
        if (!isWebNotCapacitor()) return;
        if (!isGetStartedDrawerOpen) return;
        if (!desktopReturnPathRef.current) {
            desktopReturnPathRef.current = `${window.location.pathname}${window.location.search}`;
        }
    }, [isGetStartedDrawerOpen]);

    // Desktop web only: browser back/forward while drawer is open.
    useEffect(() => {
        if (!isWebNotCapacitor()) return;

        const onPopState = () => {
            if (!isGetStartedDrawerOpen) return;
            const path = window.location.pathname.replace(/\/+$/, "") || "/";
            if (path === "/faq") {
                if (externalIsOpen === undefined) setInternalIsOpen(true);
                setActiveView("faq");
                return;
            }
            if (path === "/glossary") {
                if (externalIsOpen === undefined) setInternalIsOpen(true);
                setActiveView("glossary");
                return;
            }
            desktopReturnPathRef.current = null;
            setActiveView("menu");
            if (externalOnClose) {
                externalOnClose();
            } else {
                setInternalIsOpen(false);
            }
        };

        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [externalOnClose]);

    // Handle feedback click to open RotoGPT feedback popup
    const handleFeedbackClick = async () => {
        // openRotoGPTFeedbackPopup(authId);
        try {
            const token = auth0IsAuthenticated
                ? await getAccessTokenSilently()
                : !isLoggedInAuth0ExperienceGlobal
                    ? NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN
                    : await getAccessTokenSilently();
            setAccessAuthToken(token);
            setIsFeedbackFormOpen(true);
        } catch {
            setAccessAuthToken(NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN);
            setIsFeedbackFormOpen(true);
        }
    };

    const handleRateAppClick = () => {
        void promptNativeAppReview();
    };

    // Get title based on active view
    const getTitle = (): string => {
        const titleMap: Record<string, string> = {
            menu: "Support Center",
            gettingStarted: "Getting Started",
            contactUs: "Contact Us",
            faq: "FAQ",
            glossary: "Glossary",
            whatsNew: "What's New",
            readTermsAndConditions: "Terms and Conditions",
            reportBug: "Report a Bug",
            suggestFeature: "Suggest a Feature",
        };
        return titleMap[activeView] || "Support Center";
    };

    return (
        <>
            {showButton && (
                <Tooltip title="Help Menu" arrow placement="top-end" slotProps={{
                    tooltip: {
                        sx: {
                            fontSize: "13px",
                        },
                    }, popper: {
                        modifiers: [
                            {
                                name: "offset",
                                options: { offset: [0, -12] },
                            },
                        ],
                    },
                }} enterTouchDelay={0}>
                    <IconButton
                        aria-label="Get Started"
                        onClick={handleOpen}
                        sx={{
                            p: 0.5,
                            borderRadius: 0,
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            backgroundColor: `transparent`,
                            border: `1px solid ${PureLightThemeCaaS.colors.alpha.black[20]}`,
                            // "&:hover": {
                            //     backgroundColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                            // },
                        }}
                    >
                        <HelpOutlineIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                </Tooltip>
            )}

            <Drawer
                anchor="bottom"
                open={isGetStartedDrawerOpen}
                onClose={handleClose}
                hideBackdrop={true}
                sx={{
                    pointerEvents: "none", // Keep consistent with other drawers
                }}
                ModalProps={{
                    keepMounted: true,
                    // No cubrir la barra superior: safe-area + 56px (misma cifra que restas en height)
                    sx: {
                        pointerEvents: "auto", // Block background scroll when drawer is open
                        top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
                        zIndex: 1500, // highest priority: HelpMenu appears above BenchDrawer, PaymentMethod and all response drawers
                    },
                }}
                PaperProps={{
                    sx: {
                        pointerEvents: "auto",
                        width: {
                            xs: "100%",
                            sm: "720px",
                            md: "720px",
                            lg: "720px",
                          },
                          margin: {
                            xs: "0",
                            sm: "0 auto",
                            md: "0 auto",
                            lg: "0 auto",
                          },
                        // height: isIOS || isAndroid ? "calc(100svh - env(safe-area-inset-top) - 56px)" : "calc(100vh  - 56px)",
                        height: isIOS || isAndroid
                            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
                            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
                        border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                    },
                }}
            >
                <Box
                    className={styles.noScrollbar}
                    sx={{
                        bgcolor: "#EDEDED",
                        height: "100%",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        // touchAction: "none", // Prevent scroll of the parent container
                        "& > *": {
                            touchAction: "pan-y", // Allow scroll only in child components
                        },
                        // ADD THIS LINE to respect the bottom safe area on all devices
                        paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
                        pointerEvents: "auto",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1000,
                            flexShrink: 0,
                            backgroundColor: "#EDEDED",
                            borderBottom: activeView === "gettingStarted" ? "none" : `1px solid ${PureLightThemeCaaS.colors.alpha.black[100]}`,
                        }}
                    >
                        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={activeView === "gettingStarted" ? 0 : 1}>
                            <IconButton
                                aria-label="back"
                                onClick={handleBack}
                                sx={{
                                    visibility: activeView === "menu" ? "hidden" : "visible",
                                }}
                            >
                                <ArrowBackIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                            </IconButton>
                            <Typography variant="h4" fontSize={"16px"} color={PureLightThemeCaaS.colors.alpha.black[100]}>
                                {getTitle()}
                            </Typography>
                            <IconButton aria-label="close" onClick={handleClose}>
                                <CloseIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, WebkitOverflowScrolling: "touch", display: "flex", flexDirection: "column" }}>
                        {activeView === "menu" && (
                            <HelpMenuMain
                                onChangeContent={handleChangeContent}
                                onFeedbackClick={handleFeedbackClick}
                                onRateAppClick={handleRateAppClick}
                            />
                        )}
                        {/* if the user is not logged in, show the getting started page */}
                        {!isLoggedInAuth0ExperienceGlobal && (
                            <>
                                {activeView === "gettingStarted" && <SecondGetStartedPage handleBack={handleBack} handleClose={handleClose} />}
                            </>
                        )}
                        {activeView === "contactUs" && (
                            <ContactUs handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "faq" && (
                            <Faq handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "glossary" && (
                            <Glossary handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "whatsNew" && (
                            <WhatsNew handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "reportBug" && (
                            <ReportBug handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "suggestFeature" && (
                            <SuggestFeature handleBack={handleBack} handleClose={handleClose} />
                        )}
                        {activeView === "readTermsAndConditions" && (
                            <ReadTermsAndConditions handleBack={handleBack} handleClose={handleClose} />
                        )}
                    </Box>
                </Box>
            </Drawer>
            <FeedbackForm
                open={isFeedbackFormOpen}
                accessAuthToken={accessAuthToken}
                onClose={() => setIsFeedbackFormOpen(false)}
            />
        </>
    );
};

export default HelpMenu;